// useFormValidation.js
import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook for form validation
 * Follows Single Responsibility Principle - only handles validation logic
 * Optimized to prevent unnecessary re-renders
 * 
 * @param {Object} validationSchema - Validation schema built from ValidationSchema class
 * @returns {Object} Validation methods and state
 * 
 * @example
 * const { errors, validateField, validateForm, touchField } = useFormValidation(schema);
 */
export const useFormValidation = (validationSchema) => {
  // State for storing validation errors
  const [errors, setErrors] = useState({});

  // State for tracking which fields have been touched (visited)
  const [touchedFields, setTouchedFields] = useState({});

  // Use ref to store schema to avoid stale closures
  const validationSchemaRef = useRef(validationSchema);

  // Update schema ref if it changes
  if (validationSchemaRef.current !== validationSchema) {
    validationSchemaRef.current = validationSchema;
  }

  /**
   * Validate a single field
   * Only updates state if error actually changes (prevents unnecessary renders)
   * 
   * @param {string} fieldName - Name of the field to validate
   * @param {any} value - Current value of the field
   * @param {Object} formData - Entire form data (for cross-field validation)
   * @returns {string|null} Error message or null if valid
   * 
   * @example
   * validateField('email', 'test@example.com', formData);
   */
const validateField = useCallback((fieldName, value, formData) => {
  if (!validationSchemaRef.current) return null;

  let error = null;

  // Handle milestone field validation
  if (fieldName.startsWith('milestones[')) {
    const match = fieldName.match(/milestones\[(\d+)\]\.(\w+)/);
    if (match) {
      const index = parseInt(match[1]);
      const milestoneFieldName = match[2];
      const milestone = formData.milestones?.[index];
      
      if (milestone) {
        // Validate individual milestone field based on field name
        switch (milestoneFieldName) {
          case 'milestoneName':
            if (!value || value.trim() === '') {
              error = 'Milestone Name is required';
            }
            break;
          case 'amount':
            if (!value || value === '') {
              error = 'Amount is required';
            } else if (Number(value) <= 0) {
              error = 'Amount must be greater than 0';
            }
            break;
          case 'currency':
            if (!value) {
              error = 'Currency is required';
            }
            break;
          case 'status':
            if (!value) {
              error = 'Status is required';
            }
            break;
          case 'milestoneStartDate':
            if (!value) {
              error = 'Start Date is required';
            }
            break;
          case 'milestoneEndDate':
            if (!value) {
              error = 'End Date is required';
            } else if (milestone.milestoneStartDate) {
              if (new Date(value) <= new Date(milestone.milestoneStartDate)) {
                error = 'End date must be after start date';
              }
            }
            break;
          default:
            break;
        }
      }
    }
  } else {
    // Regular field validation
    error = validationSchemaRef.current.validateField(
      fieldName,
      value,
      formData
    );
  }

  // Update errors state only if error changed
  setErrors((prevErrors) => {
    if (prevErrors[fieldName] === error) return prevErrors;

    if (error) {
      return { ...prevErrors, [fieldName]: error };
    } else {
      const { [fieldName]: removed, ...rest } = prevErrors;
      return rest;
    }
  });

  return error;
}, []);

  /**
   * Validate entire form
   * Returns object with isValid flag and all errors
   * 
   * @param {Object} formData - All form data to validate
   * @returns {Object} { isValid: boolean, errors: Object }
   * 
   * @example
   * const { isValid, errors } = validateForm(formData);
   * if (!isValid) {
   *   console.log('Errors:', errors);
   * }
   */
  const validateForm = useCallback((formData) => {
    if (!validationSchemaRef.current) {
      return { isValid: true, errors: {} };
    }

    // Validate all fields at once
    const result = validationSchemaRef.current.validateAll(formData);

    // Update errors state with all validation results
    setErrors(result.errors);

    return result;
  }, []);

  /**
   * Mark field as touched (visited/blurred)
   * Used to determine which errors to show
   * Only show errors for touched fields to avoid overwhelming user
   * 
   * @param {string} fieldName - Name of field to mark as touched
   * 
   * @example
   * touchField('email'); // Now errors for email will be visible
   */
  const touchField = useCallback((fieldName) => {
    setTouchedFields((prev) => {
      // If already touched, don't update state (prevents re-render)
      if (prev[fieldName]) return prev;
      return { ...prev, [fieldName]: true };
    });
  }, []);

  /**
   * Mark multiple fields as touched at once
   * Useful for showing all errors on form submission
   * 
   * @param {string[]} fieldNames - Array of field names to mark as touched
   * 
   * @example
   * touchFields(['email', 'password', 'confirmPassword']);
   */
  const touchFields = useCallback((fieldNames) => {
    setTouchedFields((prev) => {
      const newTouched = { ...prev };
      let hasChanges = false;

      fieldNames.forEach(fieldName => {
        if (!newTouched[fieldName]) {
          newTouched[fieldName] = true;
          hasChanges = true;
        }
      });

      // Only update if there were actual changes
      return hasChanges ? newTouched : prev;
    });
  }, []);

  /**
   * Mark all fields in form as touched
   * Useful when showing all errors on submit
   * 
   * @param {Object} formData - Form data to extract field names from
   * 
   * @example
   * touchAllFields(formData); // Mark all fields as touched
   */
  const touchAllFields = useCallback((formData) => {
    const allFieldNames = Object.keys(formData);
    touchFields(allFieldNames);
  }, [touchFields]);

  /**
   * Clear error for a specific field
   * 
   * @param {string} fieldName - Name of field to clear error for
   * 
   * @example
   * clearFieldError('email');
   */
  const clearFieldError = useCallback((fieldName) => {
    setErrors((prevErrors) => {
      // If no error exists, don't update state
      if (!prevErrors[fieldName]) return prevErrors;

      const { [fieldName]: removed, ...rest } = prevErrors;
      return rest;
    });
  }, []);

  /**
   * Clear all errors and reset touched state
   * Useful when resetting the form
   * 
   * @example
   * clearAllErrors(); // Clean slate
   */
  const clearAllErrors = useCallback(() => {
    setErrors({});
    setTouchedFields({});
  }, []);

  /**
   * Get error for a field (only if touched)
   * This is the main method to use when displaying errors
   * 
   * @param {string} fieldName - Name of field
   * @returns {string|undefined} Error message or undefined
   * 
   * @example
   * const emailError = getFieldError('email');
   * {emailError && <span className="error">{emailError}</span>}
   */
  const getFieldError = useCallback((fieldName) => {
    return touchedFields[fieldName] ? errors[fieldName] : undefined;
  }, [errors, touchedFields]);

  /**
   * Check if field has an error (touched + has error)
   * Useful for conditional styling
   * 
   * @param {string} fieldName - Name of field
   * @returns {boolean} True if field is touched and has error
   * 
   * @example
   * <input className={hasError('email') ? 'input-error' : ''} />
   */
  const hasError = useCallback((fieldName) => {
    return Boolean(touchedFields[fieldName] && errors[fieldName]);
  }, [errors, touchedFields]);

  /**
   * Check if field has been touched
   * 
   * @param {string} fieldName - Name of field
   * @returns {boolean} True if field has been touched
   * 
   * @example
   * if (isTouched('email')) {
   *   // Show error or validation message
   * }
   */
  const isTouched = useCallback((fieldName) => {
    return Boolean(touchedFields[fieldName]);
  }, [touchedFields]);

  /**
   * Get raw error without checking touched state
   * Useful for displaying errors regardless of touched state
   * 
   * @param {string} fieldName - Name of field
   * @returns {string|undefined} Error message or undefined
   * 
   * @example
   * const rawError = getRawError('email');
   */
  const getRawError = useCallback((fieldName) => {
    return errors[fieldName];
  }, [errors]);

  /**
   * Check if form has any errors
   * 
   * @returns {boolean} True if there are any errors
   * 
   * @example
   * if (hasErrors()) {
   *   console.log('Form has errors');
   * }
   */
  const hasErrors = useCallback(() => {
    return Object.keys(errors).length > 0;
  }, [errors]);

  /**
   * Check if form is valid (no errors)
   * 
   * @returns {boolean} True if no errors
   * 
   * @example
   * if (isFormValid()) {
   *   submitForm();
   * }
   */
  const isFormValid = useCallback(() => {
    return Object.keys(errors).length === 0;
  }, [errors]);

  /**
   * Untouch a field (mark as not visited)
   * Useful for dynamic forms
   * 
   * @param {string} fieldName - Name of field to untouch
   * 
   * @example
   * untouchField('optionalField');
   */
  const untouchField = useCallback((fieldName) => {
    setTouchedFields((prev) => {
      if (!prev[fieldName]) return prev;
      const { [fieldName]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Reset validation state completely
   * Clears errors and touched fields
   * 
   * @example
   * reset(); // Start fresh
   */
  const reset = useCallback(() => {
    setErrors({});
    setTouchedFields({});
  }, []);

  // Return all methods and state
  return {
    // State
    setErrors,
    errors,                 // All current errors { fieldName: errorMessage }
    touchedFields,          // All touched fields { fieldName: true }

    // Validation methods
    validateField,          // Validate single field
    validateForm,           // Validate entire form

    // Touch management
    touchField,             // Mark single field as touched
    touchFields,            // Mark multiple fields as touched
    touchAllFields,         // Mark all fields as touched
    untouchField,           // Unmark field as touched
    isTouched,              // Check if field is touched

    // Error management
    clearFieldError,        // Clear error for one field
    clearAllErrors,         // Clear all errors and touched state
    getFieldError,          // Get error (only if touched)
    getRawError,            // Get error (even if not touched)
    hasError,               // Check if field has error (touched + error)
    hasErrors,              // Check if form has any errors
    isFormValid,            // Check if form is valid

    // Utility
    reset,                  // Complete reset
  };
};